import { useStrings } from "@1js/localization-components";
import type {
  AccordionToggleEventHandler,
  DialogOpenChangeData,
  DialogOpenChangeEvent,
} from "@fluentui/react-components";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { Dismiss24Regular, Filter20Regular } from "@fluentui/react-icons";
import * as React from "react";
import type { FacetDictionary } from "../data";
import {
  MobileApplyFilterLabel,
  MobileClearFilterLabel,
  MobileFilterButtonLabel,
  MobileFilterLabel,
} from "./PromptFilter.strings";
import { usePromptFilterLocalizations } from "./usePromptFilterStrings";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "flex-start",
    alignSelf: "stretch",
    justifyContent: "center",
    ...shorthands.gap("0.5rem"),

    "@media (max-width: 540px)": {
      alignItems: "center",
    },
  },

  dialogBody: {
    maxHeight: "calc(100vh - 84px)",
  },

  filterHeader: {
    fontSize: "0.875rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "-0.04375rem",
  },

  filterList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },

  checkbox: {
    paddingTop: "8px",
    paddingBottom: "8px",
  },

  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
});

export type PromptFiltersMobileProps = {
  selectedFacets: Record<string, string[]>;
  facets: FacetDictionary;
  disabled?: boolean;
  onApplyFiltered: (selectedFacets: Record<string, string[]>) => void;
};

// Build a facet values map based on the facets and selected facets
const buildFacetValueMapping = (
  facets: FacetDictionary,
  selectedFacets: Record<string, string[]>
) => {
  const facetValuesMap: Record<string, Record<string, boolean>> = {};
  Object.entries(facets).forEach(([facetKey, facetValues]) => {
    facetValuesMap[facetKey] = {};
    facetValues.forEach((facetValue) => {
      facetValuesMap[facetKey][facetValue.value] =
        selectedFacets[facetKey]?.includes(facetValue.value) ?? false;
    });
  });

  return facetValuesMap;
};

export const PromptFiltersMobile: React.FC<PromptFiltersMobileProps> = ({
  facets,
  disabled,
  selectedFacets,
  onApplyFiltered,
}) => {
  const styles = useStyles();
  const { facetKeyToLabel, facetValueToLabel } = usePromptFilterLocalizations();
  const localizations = useStrings({
    MobileFilterButtonLabel,
    MobileFilterLabel,
    MobileClearFilterLabel,
    MobileApplyFilterLabel,
  });
  const [facetValuesMap, setFacetValuesMap] = React.useState<
    Record<string, Record<string, boolean>>
  >({});

  const [openItems, setOpenItems] = React.useState(
    Object.entries(facets).map(([facetKey, _]) => facetKey)
  );

  // Update the facet values map based on the selected facets and facet values
  React.useEffect(() => {
    setFacetValuesMap(buildFacetValueMapping(facets, selectedFacets));
  }, [facets, selectedFacets]);

  const handleToggle: AccordionToggleEventHandler = (_event, data) => {
    setOpenItems((prevOpenItems) => {
      const itemIndex = prevOpenItems.indexOf(data.value as string);
      if (itemIndex === -1) {
        return [...prevOpenItems, data.value as string];
      } else {
        return prevOpenItems.filter((_, index) => index !== itemIndex);
      }
    });
  };

  // set all map values to false
  const handleClearAllClicked = () => {
    const facetValuesMap: Record<string, Record<string, boolean>> = {};
    Object.entries(facets).forEach(([facetKey, facetValues]) => {
      facetValuesMap[facetKey] = {};
      facetValues.forEach((facetValue) => {
        facetValuesMap[facetKey][facetValue.value] = false;
      });
    });
    setFacetValuesMap(facetValuesMap);
  };

  // transform facet values map to selected facets
  const handleApplyClicked = () => {
    const output: Record<string, string[]> = {};
    Object.entries(facetValuesMap).forEach(([key, value]) => {
      output[key] = Object.keys(value).filter((innerKey) => value[innerKey]);
    });

    onApplyFiltered(output);
  };

  return (
    <Dialog
      onOpenChange={(
        _event: DialogOpenChangeEvent,
        data: DialogOpenChangeData
      ) => {
        if (!data.open) {
          setFacetValuesMap(buildFacetValueMapping(facets, selectedFacets));
        }
      }}
    >
      <DialogTrigger disableButtonEnhancement>
        <div className={styles.container}>
          <Button
            icon={<Filter20Regular />}
            size="medium"
            aria-label={localizations.MobileFilterButtonLabel()}
          />
        </div>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            }
          >
            {localizations.MobileFilterLabel()}
          </DialogTitle>
          <DialogContent>
            <Accordion
              multiple
              collapsible
              openItems={openItems}
              onToggle={handleToggle}
            >
              {Object.entries(facetValuesMap).map(([facetKey, faceValues]) => (
                <AccordionItem value={facetKey} key={facetKey}>
                  <AccordionHeader
                    expandIconPosition="end"
                    className={styles.filterHeader}
                  >
                    {facetKeyToLabel(facetKey)}
                  </AccordionHeader>
                  <AccordionPanel className={styles.filterList}>
                    {Object.entries(faceValues as Record<string, boolean>).map(
                      (value: [string, boolean]) => (
                        <Checkbox
                          size="large"
                          className={styles.checkbox}
                          label={facetValueToLabel(value[0])}
                          value={`${facetKey}-${value[0]}`}
                          key={`${facetKey}-${value[0]}`}
                          checked={value[1]}
                          onChange={(_, data) => {
                            const updatedFacets: Record<
                              string,
                              Record<string, boolean>
                            > = {
                              ...facetValuesMap,
                              [facetKey]: {
                                ...facetValuesMap[facetKey],
                                [value[0]]: !!data.checked,
                              },
                            };
                            setFacetValuesMap(updatedFacets);
                          }}
                        />
                      )
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </DialogContent>
        </DialogBody>
        <DialogActions className={styles.actions}>
          <Button
            appearance="subtle"
            size="large"
            disabled={disabled}
            onClick={handleClearAllClicked}
          >
            {localizations.MobileClearFilterLabel()}
          </Button>
          <DialogTrigger action="close">
            <Button
              appearance="outline"
              size="large"
              disabled={disabled}
              onClick={handleApplyClicked}
            >
              {localizations.MobileApplyFilterLabel()}
            </Button>
          </DialogTrigger>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
